import { MessageCircle, Check, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";
import { Icon, Eyebrow } from "@/components/site/ui";
import { SERVICES, CONTACT, waLink } from "@/data/site";

const STEPS = [
  { no: "01", t: "Keşif", d: "Markanı, hedeflerini ve kitleni analiz ediyoruz." },
  { no: "02", t: "Çekim", d: "Profesyonel ekipmanla içerik üretimi ve prodüksiyon." },
  { no: "03", t: "Kurgu", d: "Reels, post ve hikaye tasarımı; motion ve ses tasarımı." },
  { no: "04", t: "Yayın", d: "Planlı paylaşım takvimi ve Meta reklam yönetimi." },
  { no: "05", t: "Analiz", d: "Aylık performans raporu ve strateji optimizasyonu." },
];

export default function Hizmetler() {
  return (
    <div className="pt-4">
      <section className="relative py-20 sm:py-28 overflow-hidden grain">
        <div className="absolute top-0 left-1/3 w-[420px] h-[420px] bg-cobalt/20 blur-[130px] rounded-full" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <Eyebrow>Hizmetler</Eyebrow>
          <h1 className="mt-6 font-display font-extrabold text-5xl sm:text-7xl leading-[0.95]">
            <MaskedLines lines={["Fikirden viral", "içeriğe"]} lineClassName="[&:nth-child(2)]:text-gradient" />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-lg text-slate-300">Markanı dijitalde büyütecek tüm hizmetler tek çatı altında. Sosyal medyadan drone çekimine, reklam yönetiminden içerik stratejisine.</p>
          </Reveal>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 sm:py-24 bg-ink-900/50 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Stagger className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <StaggerItem key={s.id}>
                <div data-testid={`hizmet-card-${s.id}`} className="group relative h-full p-8 rounded-3xl bg-ink-700 border border-white/10 hover:border-neon-magenta/50 transition-colors overflow-hidden">
                  <div className="absolute -right-16 -top-16 w-40 h-40 bg-neon-purple/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between">
                    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cobalt to-neon-magenta">
                      <Icon name={s.icon} className="w-7 h-7 text-white" />
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold">{s.title}</h3>
                  <p className="mt-2 text-slate-400 leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-neon-pink shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={waLink(CONTACT.phoneRaw, `Merhaba, "${s.title}" hizmeti hakkında bilgi almak istiyorum.`)}
                    target="_blank" rel="noreferrer"
                    data-testid={`hizmet-cta-${s.id}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-neon-pink hover:text-white transition-colors"
                  >
                    Bu hizmet için yaz <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-2xl">
            <Eyebrow>Nasıl Çalışıyoruz?</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold">5 adımda <span className="text-gradient">büyüme</span></h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map((st, i) => (
              <Reveal key={st.no} delay={i * 0.08}>
                <div className="relative h-full p-6 rounded-2xl glass border border-white/10">
                  <div className="font-display text-4xl font-extrabold text-transparent [-webkit-text-stroke:1.5px_#0052FF]">{st.no}</div>
                  <h3 className="mt-3 font-bold text-lg">{st.t}</h3>
                  <p className="mt-1 text-sm text-slate-400">{st.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal className="neon-border rounded-3xl glass-strong p-10 text-center glow-blue">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Hangi hizmete ihtiyacın var?</h2>
            <p className="mt-3 text-slate-300">Sana özel çözüm için hemen WhatsApp'tan yaz, ücretsiz görüşelim.</p>
            <a
              href={waLink(CONTACT.phoneRaw, "Merhaba İzmir Medya, hizmetleriniz hakkında bilgi almak istiyorum.")}
              target="_blank" rel="noreferrer"
              data-testid="hizmetler-cta-whatsapp"
              className="mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white font-semibold text-lg hover:scale-[1.03] transition-transform"
            >
              <MessageCircle className="w-5 h-5" /> Ücretsiz Görüşme Başlat
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
